<script setup>
import { computed, watch, onMounted, onBeforeUnmount, ref } from 'vue';
import { resolveAceleresApiBase } from 'shared/helpers/aceleresApiBase';
import {
  useMapGetter,
  useFunctionGetter,
  useStore,
} from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useMediaQuery } from '@vueuse/core';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { AsYouType } from 'libphonenumber-js';
import axios from 'axios';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import TeamNumbersAPI from 'dashboard/api/aceleres/teamNumbers';

import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import ConversationAction from './ConversationAction.vue';
import ContactInfo from './contact/ContactInfo.vue';
import AceleresOwnershipSummary from './AceleresOwnershipSummary.vue';
import ContactCampaign from './contact/ContactCampaign.vue';
import GroupMembers from './contact/GroupMembers.vue';
import ContactNotes from './contact/ContactNotes.vue';
import ConversationInfo from './ConversationInfo.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';
import ContactClassificationAttribute from './customAttributes/ContactClassificationAttribute.vue';
import MacrosList from './Macros/List.vue';
import ShopifyOrdersList from 'dashboard/components/widgets/conversation/ShopifyOrdersList.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'next/avatar/Avatar.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import { vOnClickOutside } from '@vueuse/components';
import LinearIssuesList from 'dashboard/components/widgets/conversation/linear/IssuesList.vue';
import LinearSetupCTA from 'dashboard/components/widgets/conversation/linear/LinearSetupCTA.vue';
import {
  CONTACT_CLASSIFICATION_ATTRIBUTE,
  CONTACT_CLASSIFICATIONS,
  resolveContactClassification,
  resolveContactClassificationSelection,
  shouldReconcileTeamNumberClassification,
} from 'dashboard/constants/contactClassifications';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
});

const { updateUISettings, isContactSidebarItemOpen } = useUISettings();

// Aceleres 2026-08-19 -- cabecalho que encolhe, so no telemovel.
//
// A ficha abre com a foto grande e centrada. Ao rolar, ela sobe para a
// esquerda e vira o circulo pequeno ao lado do nome, no cabecalho; ao voltar
// ao topo desfaz o caminho. Quem manda no desenho e ESTE numero, publicado
// como variavel CSS na raiz do painel: os dois lados (a foto grande, dentro do
// ContactInfo, e o par foto+nome do cabecalho) leem a mesma progressao, entao
// nao ha duas animacoes para sincronizar.
//
// A distancia e a altura util da foto no telemovel (96px) menos o respiro que
// ela ja tem: aos ~88px de rolagem a transicao esta completa, que e mais ou
// menos quando a foto sairia do ecra sozinha.
const HERO_COLAPSO_PX = 88;
const heroProgresso = ref(0);

const onPanelBodyScroll = event => {
  const topo = event.target?.scrollTop || 0;
  heroProgresso.value = Math.min(1, Math.max(0, topo / HERO_COLAPSO_PX));
};

const contactInfoRef = ref(null);
const contactClassificationStaticElements = CONTACT_CLASSIFICATIONS.map(
  ({ value }) => ({
    key: `static-contact-classification-${value}`,
    type: 'static_attribute',
    replaceAttributeLabel: 'Número de equipe',
    alwaysVisible: true,
    classificationValue: value,
  })
);
// A lista de accordions abaixo pertence à antiga aba "Detalhes" e agora é o
// conteúdo da aba "Ações". Ela continua montada para preservar estado,
// notas, atributos, campanhas, macros e seus eventos; apenas fica invisível
// enquanto o usuário consulta as novas abas cadastrais/históricas.
const activeContactTab = ref('actions');
const onContactTabChanged = tab => {
  activeContactTab.value = tab;
};
const groupMembersRef = ref(null);
const groupSettingsRef = ref(null);
const isMobileViewport = useMediaQuery('(max-width: 767px)');
const liveWhatsappGroup = ref(null);
const showClearConversationModal = ref(false);
const isClearingConversation = ref(false);
const showBlockConversationModal = ref(false);
const isUpdatingConversationBlock = ref(false);
const showLeaveGroupModal = ref(false);
const isLeavingWhatsappGroup = ref(false);
const showDeleteConversationModal = ref(false);
const isDeletingConversation = ref(false);
const { t } = useI18n();
const route = useRoute();

const shopifyIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'shopify'
);

const isShopifyFeatureEnabled = computed(
  () => shopifyIntegration.value.enabled
);

const { isCloudFeatureEnabled } = useAccount();

const isLinearFeatureEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.LINEAR)
);

const linearIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'linear'
);

const isLinearClientIdConfigured = computed(() => {
  return !!linearIntegration.value?.id;
});

const isLinearConnected = computed(
  () => linearIntegration.value?.enabled || false
);

const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const currentUser = useMapGetter('getCurrentUser');
const conversationId = computed(() => props.conversationId);
const conversationMetadataGetter = useMapGetter(
  'conversationMetadata/getConversationMetadata'
);
const currentConversationMetaData = computed(() =>
  conversationMetadataGetter.value(conversationId.value)
);
const conversationAdditionalAttributes = computed(
  () => currentConversationMetaData.value.additional_attributes || {}
);
const conversationCampaign = computed(() => currentChat.value.campaign || {});
const conversationCustomAttributes = computed(
  () => currentChat.value.custom_attributes || {}
);
const groupClearConversationLabel = 'Limpar conversa';
const groupLeaveLabel = 'Sair do grupo';
const clearConversationLabel = 'Limpar conversa';
const clearConversationTitle = 'Deseja limpar esta conversa?';
const clearConversationDescription =
  'As mensagens serão removidas de todos os dispositivos.';
const confirmClearConversationLabel = 'Limpar';
const clearingConversationLabel = 'Limpando...';
const leaveGroupTitle = 'Deseja sair deste grupo?';
const leaveGroupDescription =
  'Você deixará o grupo no WhatsApp e deixará de receber novas mensagens.';
const confirmLeaveGroupLabel = 'Sair do grupo';
const leavingGroupLabel = 'Saindo...';
const deleteConversationLabel = 'Apagar conversa';
const deleteConversationDescription =
  'As mensagens serão removidas de todos os dispositivos.';
const cancelDeleteConversationLabel = 'Cancelar';
const confirmDeleteConversationLabel = 'Apagar';
const deletingConversationLabel = 'Apagando...';
const groupCreatedByLabel = 'Grupo criado por';
const groupNumberUnavailableLabel = 'número não disponível';
const groupCreatedSeparator = '·';
const contactPanelAccordionTitles = Object.freeze({
  notes: 'Notas do contato',
  attributes: 'Atributos do contato',
  conversationInfo: 'Informações da conversa',
  conversationActions: 'Ações da conversa',
  campaigns: 'Campanhas',
});
const CONTACT_SIDEBAR_ACCORDION_KEYS = Object.freeze([
  'is_conv_actions_open',
  'is_conv_details_open',
  'is_contact_attributes_open',
  'is_previous_conv_open',
  'is_macro_open',
  'is_linear_issues_open',
  'is_shopify_orders_open',
  'is_contact_notes_open',
  'is_campaign_info_open',
]);

const getClosedContactSidebarItems = () =>
  CONTACT_SIDEBAR_ACCORDION_KEYS.reduce((settings, key) => {
    settings[key] = false;
    return settings;
  }, {});

const resetContactSidebarItems = () => {
  updateUISettings(getClosedContactSidebarItems());
};

const toggleSidebarUIState = key => {
  const settings = getClosedContactSidebarItems();

  if (!isContactSidebarItemOpen(key)) {
    settings[key] = true;
  }

  updateUISettings(settings);
};

const channelType = computed(() => currentChat.value.meta?.channel);

const contactGetter = useMapGetter('contacts/getContact');
const contactId = computed(() => currentChat.value.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value));
const contactClassificationUpdating = ref(false);
const updateContactClassification = async value => {
  if (!contact.value?.id || contactClassificationUpdating.value) return;
  const currentValue = resolveContactClassification(contact.value).value;
  const nextValue = resolveContactClassificationSelection(contact.value, value);
  const reconcileTeamNumber = shouldReconcileTeamNumberClassification(
    contact.value,
    value
  );
  if (nextValue === currentValue && !reconcileTeamNumber) return;

  contactClassificationUpdating.value = true;
  try {
    if (reconcileTeamNumber) {
      await TeamNumbersAPI.mark(contact.value.id);
      await store.dispatch('contacts/show', { id: contact.value.id });
    } else {
      await store.dispatch('contacts/update', {
        id: contact.value.id,
        customAttributes: {
          [CONTACT_CLASSIFICATION_ATTRIBUTE]: nextValue,
        },
      });
    }
    const selectedClassification = CONTACT_CLASSIFICATIONS.find(
      option => option.value === nextValue
    );
    useAlert(`Contato classificado como ${selectedClassification.label}`);
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.message ||
        'Não foi possível alterar a classificação do contato'
    );
  } finally {
    contactClassificationUpdating.value = false;
  }
};
const contactAdditionalAttributes = computed(
  () => contact.value?.additional_attributes || {}
);
const groupAttributes = computed(() => ({
  ...(currentChat.value?.meta?.sender?.additional_attributes || {}),
  ...(currentChat.value?.meta?.sender?.custom_attributes || {}),
  ...(contact.value?.additional_attributes || {}),
  ...(contact.value?.custom_attributes || {}),
}));
// Chave de identidade do participante entre as duas respostas: o telefone
// (só dígitos) e, na falta dele, o `@lid`. É o mesmo par que o roster e a
// resposta viva nomeiam igual.
const participantKeys = participant => {
  const phone = String(participant?.phone_number || '').replace(/\D/g, '');
  const id = String(participant?.id || '');
  return [phone && `p:${phone}`, id && `i:${id}`].filter(Boolean);
};

// A foto do participante NÃO existe na resposta viva: ela é enriquecimento
// nosso, feito no serializer do contato (Aceleres::GroupParticipantAvatars)
// a partir do contato individual da pessoa. O merge raso `{...stored,
// ...live}` trocava a lista inteira de participantes pela versão viva, e a
// foto aparecia no primeiro paint e sumia quando a resposta viva chegava.
//
// A resposta viva continua mandando em tudo o mais — inclusive no NOME, que é
// por conexão (a agenda daquela instância) e por isso não pode voltar ao
// roster global.
const mergeParticipants = (storedGroup, liveGroup) => {
  const live = liveGroup.participants;
  if (!Array.isArray(live)) return live;

  const thumbnails = new Map();
  (storedGroup.participants || []).forEach(participant => {
    if (!participant?.thumbnail) return;
    participantKeys(participant).forEach(key => {
      if (!thumbnails.has(key)) thumbnails.set(key, participant.thumbnail);
    });
  });
  if (!thumbnails.size) return live;

  return live.map(participant => {
    if (participant?.thumbnail) return participant;
    const key = participantKeys(participant).find(k => thumbnails.has(k));
    return key
      ? { ...participant, thumbnail: thumbnails.get(key) }
      : participant;
  });
};

const whatsappGroup = computed(() => {
  const storedGroup = groupAttributes.value.whatsapp_group || {};
  const liveGroup = liveWhatsappGroup.value;

  if (!liveGroup || liveGroup.jid !== storedGroup.jid) return storedGroup;

  return {
    ...storedGroup,
    ...liveGroup,
    participants: mergeParticipants(storedGroup, liveGroup),
  };
});
const isWhatsappGroup = computed(
  () =>
    groupAttributes.value.is_whatsapp_group === true ||
    groupAttributes.value.is_whatsapp_group === 'true'
);
const contactPanelTitle = computed(() =>
  isWhatsappGroup.value ? 'Dados do grupo' : 'Dados do contato'
);
const deleteConversationContactLabel = computed(() => {
  const sender = currentChat.value?.meta?.sender || {};
  return sender.phone_number || sender.name || sender.identifier || '';
});
const deleteConversationTitle = computed(() => {
  const contactLabel = deleteConversationContactLabel.value;
  return contactLabel
    ? `Deseja apagar a conversa com ${contactLabel}?`
    : 'Deseja apagar a conversa?';
});
const hasLeftWhatsappGroup = ref(false);
const groupCreator = computed(() => {
  const group = whatsappGroup.value;
  const creator =
    group.created_by ||
    group.createdBy ||
    group.creator ||
    group.owner ||
    group.owner_phone ||
    group.owner_phone_number ||
    group.participants?.find(
      participant =>
        participant?.is_owner ||
        participant?.is_creator ||
        participant?.role === 'owner' ||
        participant?.role === 'creator'
    );

  if (!creator) return { name: '', phone: '' };
  if (typeof creator === 'string') return { name: '', phone: creator };

  return {
    name:
      creator.name ||
      creator.push_name ||
      creator.pushName ||
      creator.notify_name ||
      '',
    phone:
      creator.phone_number ||
      creator.phoneNumber ||
      creator.number ||
      creator.id ||
      '',
  };
});
const groupCreatedByName = computed(() => groupCreator.value.name);
const groupCreatedByPhone = computed(() => {
  const rawPhone = String(groupCreator.value.phone || '').replace(/@.*/, '');
  if (!rawPhone) return '';

  const digits = rawPhone.replace(/\D/g, '');
  if (!digits) return rawPhone;

  const formatted = digits.startsWith('55')
    ? new AsYouType('BR').input(`+${digits}`)
    : new AsYouType().input(`+${digits}`);

  return formatted || `+${digits}`;
});
// A data de criação do GRUPO só pode vir do grupo. O `created_at` da conversa
// já esteve nesta lista como último recurso e era uma resposta errada com cara
// de certa: conversa nasce quando a primeira mensagem chega, não quando o grupo
// foi criado — e, sendo epoch em segundos, ainda era lido como milissegundos e
// exibido como 1970.
const groupCreatedAt = computed(
  () =>
    whatsappGroup.value.created_at ||
    whatsappGroup.value.createdAt ||
    whatsappGroup.value.creation ||
    whatsappGroup.value.creation_date ||
    whatsappGroup.value.creation_time ||
    groupAttributes.value.whatsapp_group_created_at ||
    ''
);
// A WAME entrega `creation` em SEGUNDOS; `new Date()` espera milissegundos.
const parseGroupDate = value => {
  if (value instanceof Date) return value;
  const numeric = typeof value === 'number' ? value : Number(value);
  if (Number.isFinite(numeric) && String(value).trim() !== '') {
    // ~1e12 é a fronteira: abaixo disso um epoch plausível está em segundos.
    return new Date(numeric < 1e12 ? numeric * 1000 : numeric);
  }
  return new Date(value);
};
const groupCreatedAtLabel = computed(() => {
  if (!groupCreatedAt.value) return 'Data não disponível';
  const date = parseGroupDate(groupCreatedAt.value);
  if (Number.isNaN(date.getTime())) return String(groupCreatedAt.value);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
});
const isConversationMuted = computed(
  () => contact.value?.blocked ?? currentChat.value?.muted ?? false
);
const blockContactLabel = computed(() => {
  return isConversationMuted.value ? 'Desbloquear' : 'Bloquear';
});
const blockContactIdentity = computed(() => {
  const name =
    contact.value?.name?.trim() ||
    currentChat.value?.meta?.sender?.name?.trim();
  if (name) return name;

  return (
    contact.value?.phone_number ||
    contact.value?.phoneNumber ||
    contact.value?.identifier ||
    currentChat.value?.meta?.sender?.phone_number ||
    currentChat.value?.meta?.sender?.phoneNumber ||
    currentChat.value?.meta?.sender?.identifier ||
    'este contato'
  );
});
const blockConversationTitle = computed(() =>
  isConversationMuted.value
    ? `Deseja desbloquear ${blockContactIdentity.value}?`
    : `Deseja bloquear ${blockContactIdentity.value}?`
);
const blockConversationDescription = computed(() =>
  isConversationMuted.value
    ? 'O contato poderá voltar a enviar mensagens no atendimento.'
    : 'O contato não poderá mais enviar mensagens no atendimento. Em conexões WhatsApp WAME, o bloqueio também será solicitado ao WhatsApp.'
);
const confirmBlockConversationLabel = computed(() =>
  isConversationMuted.value ? 'Desbloquear' : 'Bloquear'
);
const updatingBlockConversationLabel = 'Atualizando...';

const getContactDetails = () => {
  if (contactId.value) {
    store.dispatch('contacts/show', { id: contactId.value });
  }
};

const refreshWhatsappGroup = group => {
  if (group?.jid) {
    liveWhatsappGroup.value = group;
  }
  getContactDetails();
};

watch(contactId, (newContactId, prevContactId) => {
  if (newContactId && newContactId !== prevContactId) {
    getContactDetails();
  }
});

watch(conversationId, () => {
  hasLeftWhatsappGroup.value = false;
  liveWhatsappGroup.value = null;
  resetContactSidebarItems();
});

const closeContactPanel = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
  });
};

const openContactEditor = () => {
  contactInfoRef.value?.toggleEditModal?.();
};

// O cabecalho do telemovel nao tem titulo: leva a seta de voltar, o par
// foto+nome que aparece com a rolagem e o menu de tres pontos.
const voltarLabel = 'Voltar';
const maisAcoesLabel = 'Mais ações';

const showHeaderMenu = ref(false);
const toggleHeaderMenu = () => {
  showHeaderMenu.value = !showHeaderMenu.value;
};
const closeHeaderMenu = () => {
  showHeaderMenu.value = false;
};

const headerMenuItems = computed(() => {
  const itens = [];
  if (contact.value?.id && !isWhatsappGroup.value) {
    itens.push({
      label: 'Editar contato',
      value: 'editar',
      action: 'editar',
      icon: 'i-lucide-pencil',
    });
  }
  return itens;
});

const onHeaderMenuAction = ({ value }) => {
  closeHeaderMenu();
  if (value === 'editar') openContactEditor();
};

const contactHeaderName = computed(
  () => contact.value?.name || contact.value?.phone_number || ''
);

const scrollToGroupSection = section => {
  const sectionElements = {
    members: groupMembersRef.value?.$el || groupMembersRef.value,
    media: contactInfoRef.value?.$el?.querySelector(
      '[data-group-section="media"]'
    ),
    settings: groupSettingsRef.value,
  };
  const element = sectionElements[section];
  element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const toggleMuteConversation = async () => {
  if (!currentChat.value?.id || isUpdatingConversationBlock.value) return;

  isUpdatingConversationBlock.value = true;
  try {
    if (isConversationMuted.value) {
      await store.dispatch('unmuteConversation', currentChat.value.id);
      useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
    } else {
      await store.dispatch('muteConversation', currentChat.value.id);
      useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
    }
    showBlockConversationModal.value = false;
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        'Não foi possível atualizar o bloqueio deste contato.'
    );
  } finally {
    isUpdatingConversationBlock.value = false;
  }
};

const openBlockConversationModal = () => {
  showBlockConversationModal.value = true;
};

const closeBlockConversationModal = () => {
  if (isUpdatingConversationBlock.value) return;
  showBlockConversationModal.value = false;
};

const clearConversation = async () => {
  if (!currentChat.value?.id) return;

  isClearingConversation.value = true;
  try {
    await ConversationAPI.clear(currentChat.value.id);
    useAlert('Conversa limpa com sucesso.');
    showClearConversationModal.value = false;
  } catch (error) {
    useAlert(
      error.response?.data?.message || 'Não foi possível limpar a conversa.'
    );
  } finally {
    isClearingConversation.value = false;
  }
};

const openClearConversationModal = () => {
  showClearConversationModal.value = true;
};

const closeClearConversationModal = () => {
  if (isClearingConversation.value) return;
  showClearConversationModal.value = false;
};

const deleteConversation = async () => {
  if (!currentChat.value?.id) return;

  isDeletingConversation.value = true;
  try {
    await store.dispatch('deleteConversation', currentChat.value.id);
    useAlert('Conversa apagada com sucesso.');
    showDeleteConversationModal.value = false;
    closeContactPanel();
  } catch (error) {
    useAlert(
      error.response?.data?.message || 'Não foi possível apagar a conversa.'
    );
  } finally {
    isDeletingConversation.value = false;
  }
};

const openDeleteConversationModal = () => {
  showDeleteConversationModal.value = true;
};

const closeDeleteConversationModal = () => {
  if (isDeletingConversation.value) return;
  showDeleteConversationModal.value = false;
};

const leaveWhatsappGroup = async () => {
  const groupJid = whatsappGroup.value?.jid;
  if (!props.inboxId || !groupJid) return;

  isLeavingWhatsappGroup.value = true;
  try {
    await axios.delete(
      `${resolveAceleresApiBase()}/api/wame/groups/${props.inboxId}`,
      {
        headers: {
          'X-Chatwoot-Token': currentUser.value?.access_token || '',
          'X-Chatwoot-Account-Id': String(route.params.accountId || ''),
        },
        data: { group_jid: groupJid },
      }
    );
    hasLeftWhatsappGroup.value = true;
    showLeaveGroupModal.value = false;
    useAlert('Você saiu do grupo no WhatsApp.');
  } catch (error) {
    useAlert(
      error.response?.data?.message || 'Não foi possível sair do grupo.'
    );
  } finally {
    isLeavingWhatsappGroup.value = false;
  }
};

const openLeaveGroupModal = () => {
  showLeaveGroupModal.value = true;
};

const closeLeaveGroupModal = () => {
  if (isLeavingWhatsappGroup.value) return;
  showLeaveGroupModal.value = false;
};

// Aceleres 2026-08-17: Esc fecha o modal de confirmacao aberto — limpar,
// bloquear, sair do grupo ou excluir. Os quatro tinham `@keydown.esc` no
// proprio backdrop, que so dispara com o foco dentro dele; como nada foca o
// backdrop, a tecla nunca chegava. Ouvir no documento, em captura, dispensa o
// foco, e consumir o evento impede que o mesmo Esc feche o painel por baixo e
// deixe o modal orfao na tela.
//
// A ordem segue a de quem esta por cima: so um deles fica aberto por vez, mas
// a lista mantem a regra explicita. Cada `close*` ja se recusa a fechar
// enquanto a acao esta em andamento, e essa guarda vale aqui tambem.
const confirmationModals = [
  { isOpen: showClearConversationModal, close: closeClearConversationModal },
  { isOpen: showBlockConversationModal, close: closeBlockConversationModal },
  { isOpen: showLeaveGroupModal, close: closeLeaveGroupModal },
  {
    isOpen: showDeleteConversationModal,
    close: closeDeleteConversationModal,
  },
];

const handleConfirmationEscape = event => {
  if (event.key !== 'Escape' || event.defaultPrevented) return;

  const open = confirmationModals.find(modal => modal.isOpen.value);
  if (!open) return;

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation?.();

  open.close();
};

onMounted(() => {
  resetContactSidebarItems();
  getContactDetails();
  store.dispatch('attributes/get', 0);
  // Load integrations to ensure linear integration state is available
  store.dispatch('integrations/get', 'linear');
  document.addEventListener('keydown', handleConfirmationEscape, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleConfirmationEscape, true);
});
</script>

<template>
  <div
    class="contact-details-panel flex h-full min-h-0 w-full min-w-0 max-w-full flex-col overflow-hidden"
    :class="{ 'contact-details-panel--hero-colapsado': heroProgresso > 0.5 }"
    :style="{ '--acl-hero-progresso': heroProgresso }"
  >
    <header
      class="contact-details-panel__header flex !h-14 flex-shrink-0 items-center justify-between gap-3 bg-white !px-3 md:!h-16 md:!gap-4 md:!px-5"
    >
      <div
        class="contact-details-panel__header-left flex min-w-0 items-center gap-3"
      >
        <!-- Aceleres 2026-08-19: no telemovel o gesto e VOLTAR (a ficha ocupa o
             ecra inteiro e a conversa fica por baixo), no computador e FECHAR o
             painel lateral. Mesmo destino, iconografia diferente — por isso sao
             dois botoes com a mesma accao, e nao um icone trocado por CSS. -->
        <Button
          v-tooltip.top="$t('GENERAL.CLOSE')"
          icon="i-lucide-arrow-left"
          ghost
          class="contact-details-panel__header-button contact-details-panel__header-button--back text-n-slate-12"
          :aria-label="voltarLabel"
          @click="closeContactPanel"
        />
        <Button
          v-tooltip.top="$t('GENERAL.CLOSE')"
          icon="i-lucide-x"
          ghost
          class="contact-details-panel__header-button contact-details-panel__header-button--close text-n-slate-12"
          @click="closeContactPanel"
        />
        <h2
          class="contact-details-panel__title m-0 truncate text-base font-semibold text-n-slate-12"
        >
          {{ contactPanelTitle }}
        </h2>
        <!-- O par foto+nome que a rolagem traz para cima. Fica sempre no DOM
             (so no telemovel) e a opacidade/posicao vem da progressao — assim a
             entrada acompanha o dedo em vez de piscar num limiar. -->
        <div class="contact-details-panel__mini" aria-hidden="true">
          <Avatar
            :src="contact?.thumbnail"
            :name="contactHeaderName"
            :size="34"
            rounded-full
            hide-offline-status
            class="contact-details-panel__mini-avatar"
          />
          <span class="contact-details-panel__mini-name">
            {{ contactHeaderName }}
          </span>
        </div>
      </div>
      <Button
        v-if="contact?.id && !isWhatsappGroup"
        v-tooltip.top="$t('EDIT_CONTACT.BUTTON_LABEL')"
        icon="i-lucide-pencil"
        ghost
        class="contact-details-panel__header-button contact-details-panel__header-button--edit text-n-slate-12"
        @click="openContactEditor"
      />
      <div
        v-if="headerMenuItems.length"
        v-on-click-outside="closeHeaderMenu"
        class="contact-details-panel__header-menu"
      >
        <Button
          icon="i-lucide-more-vertical"
          ghost
          class="contact-details-panel__header-button contact-details-panel__header-button--menu text-n-slate-12"
          :aria-label="maisAcoesLabel"
          @click="toggleHeaderMenu"
        />
        <DropdownMenu
          v-if="showHeaderMenu"
          :menu-items="headerMenuItems"
          class="contact-details-panel__header-menu-list"
          @action="onHeaderMenuAction"
        />
      </div>
    </header>
    <div
      class="contact-details-panel__body min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain"
      @scroll.passive="onPanelBodyScroll"
    >
      <ContactInfo
        ref="contactInfoRef"
        :contact="contact"
        :whatsapp-group-data="whatsappGroup"
        :channel-type="channelType"
        :inbox-id="inboxId"
        @contact-tab-changed="onContactTabChanged"
        @group-section-navigate="scrollToGroupSection"
        @group-updated="refreshWhatsappGroup"
      />
      <AceleresOwnershipSummary />
      <!--
        IMPORTANTE: este bloco NÃO deve ser apagado nem movido para outra aba.
        Ele contém as funcionalidades legadas do modal: Notas do contato,
        atributos, informações da conversa, ações da conversa, campanhas,
        Macros e demais accordions que possam existir abaixo.

        Antes da separação das abas, ele aparecia sempre porque vive no
        ContactPanel, irmão do ContactInfo. Por isso Notas até Macros também
        eram exibidos quando o usuário selecionava Detalhes ou Histórico.
        Mantemos tudo montado com v-show para preservar estado e listeners;
        apenas ocultamos visualmente o bloco fora da aba Ações.
      -->
      <div
        v-show="activeContactTab === 'actions'"
        class="px-4 pb-8 list-group"
        :class="{
          'contact-details-panel__actions-hidden':
            activeContactTab !== 'actions',
        }"
      >
        <div
          class="contact-details-panel__accordion-list flex flex-col !gap-2 md:!gap-5"
        >
          <AccordionItem
            class="contact-details-panel__accordion contact-details-panel__accordion--notes"
            :title="contactPanelAccordionTitles.notes"
            :is-open="isContactSidebarItemOpen('is_contact_notes_open')"
            compact
            show-chevron
            animated
            :draggable="false"
            @toggle="toggleSidebarUIState('is_contact_notes_open')"
          >
            <template #icon>
              <span
                class="i-lucide-sticky-note contact-details-panel__accordion-icon"
              />
            </template>
            <ContactNotes :contact-id="contactId" />
          </AccordionItem>

          <AccordionItem
            class="contact-details-panel__accordion contact-details-panel__accordion--attributes"
            :title="contactPanelAccordionTitles.attributes"
            :is-open="isContactSidebarItemOpen('is_contact_attributes_open')"
            compact
            show-chevron
            animated
            :draggable="false"
            @toggle="toggleSidebarUIState('is_contact_attributes_open')"
          >
            <template #icon>
              <span
                class="i-lucide-tags contact-details-panel__accordion-icon"
              />
            </template>
            <CustomAttributes
              attribute-type="contact_attribute"
              attribute-from="conversation_contact_panel"
              :contact-id="contact.id"
              :static-elements="contactClassificationStaticElements"
              :empty-state-message="
                $t('CONVERSATION_CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND')
              "
            >
              <template #staticItem="{ element }">
                <ContactClassificationAttribute
                  v-if="element.classificationValue"
                  :contact-id="contact.id"
                  :classification-value="element.classificationValue"
                  :disabled="contactClassificationUpdating"
                  @select="updateContactClassification"
                />
              </template>
            </CustomAttributes>
          </AccordionItem>

          <AccordionItem
            class="contact-details-panel__accordion contact-details-panel__accordion--conversation-info"
            :title="contactPanelAccordionTitles.conversationInfo"
            :is-open="isContactSidebarItemOpen('is_conv_details_open')"
            compact
            show-chevron
            animated
            :draggable="false"
            @toggle="toggleSidebarUIState('is_conv_details_open')"
          >
            <template #icon>
              <span
                class="i-lucide-info contact-details-panel__accordion-icon"
              />
            </template>
            <ConversationInfo
              :conversation-attributes="conversationAdditionalAttributes"
              :contact-attributes="contactAdditionalAttributes"
            />
          </AccordionItem>

          <div class="conversation--actions">
            <AccordionItem
              class="contact-details-panel__accordion contact-details-panel__accordion--actions"
              :title="contactPanelAccordionTitles.conversationActions"
              :is-open="isContactSidebarItemOpen('is_conv_actions_open')"
              show-chevron
              animated
              :draggable="false"
              @toggle="toggleSidebarUIState('is_conv_actions_open')"
            >
              <template #icon>
                <span
                  class="i-lucide-settings-2 contact-details-panel__accordion-icon"
                />
              </template>
              <ConversationAction
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </AccordionItem>
          </div>

          <AccordionItem
            class="contact-details-panel__accordion contact-details-panel__accordion--campaign"
            :title="contactPanelAccordionTitles.campaigns"
            :is-open="isContactSidebarItemOpen('is_campaign_info_open')"
            compact
            show-chevron
            animated
            :draggable="false"
            @toggle="toggleSidebarUIState('is_campaign_info_open')"
          >
            <template #icon>
              <span
                class="i-lucide-megaphone contact-details-panel__accordion-icon"
              />
            </template>
            <ContactCampaign
              :contact="contact"
              :conversation-campaign="conversationCampaign"
              :conversation-attributes="conversationAdditionalAttributes"
              :conversation-custom-attributes="conversationCustomAttributes"
            />
          </AccordionItem>

          <woot-feature-toggle feature-key="macros">
            <AccordionItem
              class="contact-details-panel__accordion contact-details-panel__accordion--macros"
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.MACROS')"
              :is-open="isContactSidebarItemOpen('is_macro_open')"
              compact
              show-chevron
              animated
              :draggable="false"
              @toggle="toggleSidebarUIState('is_macro_open')"
            >
              <template #icon>
                <span
                  class="i-lucide-bolt contact-details-panel__accordion-icon"
                />
              </template>
              <MacrosList :conversation-id="conversationId" />
            </AccordionItem>
          </woot-feature-toggle>

          <AccordionItem
            v-if="isLinearFeatureEnabled && isLinearClientIdConfigured"
            class="contact-details-panel__accordion contact-details-panel__accordion--linear"
            :title="$t('CONVERSATION_SIDEBAR.ACCORDION.LINEAR_ISSUES')"
            :is-open="isContactSidebarItemOpen('is_linear_issues_open')"
            compact
            show-chevron
            animated
            :draggable="false"
            @toggle="toggleSidebarUIState('is_linear_issues_open')"
          >
            <template #icon>
              <span
                class="i-lucide-git-branch contact-details-panel__accordion-icon"
              />
            </template>
            <LinearSetupCTA v-if="!isLinearConnected" />
            <LinearIssuesList v-else :conversation-id="conversationId" />
          </AccordionItem>

          <AccordionItem
            v-if="isShopifyFeatureEnabled"
            class="contact-details-panel__accordion contact-details-panel__accordion--shopify"
            :title="$t('CONVERSATION_SIDEBAR.ACCORDION.SHOPIFY_ORDERS')"
            :is-open="isContactSidebarItemOpen('is_shopify_orders_open')"
            compact
            show-chevron
            animated
            :draggable="false"
            @toggle="toggleSidebarUIState('is_shopify_orders_open')"
          >
            <template #icon>
              <span
                class="i-lucide-shopping-bag contact-details-panel__accordion-icon"
              />
            </template>
            <ShopifyOrdersList :contact-id="contactId" />
          </AccordionItem>
        </div>
      </div>
      <Teleport
        v-if="isWhatsappGroup"
        to="#contact-group-members-mobile-target"
        :disabled="!isMobileViewport"
        defer
      >
        <GroupMembers
          ref="groupMembersRef"
          class="max-md:scroll-mt-16"
          :group="whatsappGroup"
          :conversation-id="conversationId"
          :inbox-id="inboxId"
          :access-token="currentUser?.access_token || ''"
          @group-updated="refreshWhatsappGroup"
        />
      </Teleport>
      <div
        ref="groupSettingsRef"
        class="contact-details-panel__footer-actions !mx-0 !gap-2 !px-4 !pb-[calc(1rem+env(safe-area-inset-bottom))] max-md:scroll-mt-16 md:!mx-2 md:!gap-5 md:!px-[10px] md:!pb-7"
      >
        <button
          type="button"
          class="contact-details-panel__footer-action contact-details-panel__footer-action--danger contact-details-panel__footer-action--hidden"
          @click="openClearConversationModal"
        >
          <span
            class="i-lucide-circle-minus contact-details-panel__footer-action-icon"
          />
          <span>
            {{
              isWhatsappGroup
                ? groupClearConversationLabel
                : clearConversationLabel
            }}
          </span>
        </button>
        <button
          v-if="isWhatsappGroup"
          type="button"
          class="contact-details-panel__footer-action contact-details-panel__footer-action--danger"
          @click="openLeaveGroupModal"
        >
          <span
            class="i-lucide-log-out contact-details-panel__footer-action-icon"
          />
          <span>{{ groupLeaveLabel }}</span>
        </button>
        <button
          v-if="!isWhatsappGroup && activeContactTab === 'actions'"
          type="button"
          class="contact-details-panel__footer-action contact-details-panel__footer-action--danger"
          :disabled="isUpdatingConversationBlock"
          @click="openBlockConversationModal"
        >
          <span
            class="i-lucide-ban contact-details-panel__footer-action-icon"
          />
          <span>{{ blockContactLabel }}</span>
        </button>
        <button
          v-if="!isWhatsappGroup || hasLeftWhatsappGroup"
          type="button"
          class="contact-details-panel__footer-action contact-details-panel__footer-action--danger contact-details-panel__footer-action--hidden"
          @click="openDeleteConversationModal"
        >
          <span
            class="i-lucide-trash-2 contact-details-panel__footer-action-icon"
          />
          <span>{{ deleteConversationLabel }}</span>
        </button>
        <p v-if="isWhatsappGroup" class="contact-details-panel__group-created">
          {{ groupCreatedByLabel }}
          <span class="contact-details-panel__group-created-value">
            <span v-if="groupCreatedByName">{{ groupCreatedByName }}</span>
            <span v-else>
              {{ groupCreatedByPhone || groupNumberUnavailableLabel }}
            </span>
          </span>
          <span aria-hidden="true">{{ groupCreatedSeparator }}</span>
          {{ groupCreatedAtLabel }}
        </p>
      </div>
      <Teleport to="body">
        <Transition name="contact-delete-modal">
          <div
            v-if="showClearConversationModal"
            class="contact-delete-modal-backdrop"
            tabindex="-1"
            @click.self="closeClearConversationModal"
            @keydown.esc.stop.prevent="closeClearConversationModal"
          >
            <div
              class="contact-delete-modal contact-delete-modal--clear"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="contact-clear-modal-title"
              aria-describedby="contact-clear-modal-description"
            >
              <div class="contact-delete-modal__content">
                <h2
                  id="contact-clear-modal-title"
                  class="contact-delete-modal__title"
                >
                  {{ clearConversationTitle }}
                </h2>
                <p
                  id="contact-clear-modal-description"
                  class="contact-delete-modal__description"
                >
                  {{ clearConversationDescription }}
                </p>
                <div class="contact-delete-modal__actions">
                  <button
                    type="button"
                    class="contact-delete-modal__cancel"
                    :disabled="isClearingConversation"
                    @click="closeClearConversationModal"
                  >
                    {{ cancelDeleteConversationLabel }}
                  </button>
                  <button
                    type="button"
                    class="contact-delete-modal__confirm"
                    :disabled="isClearingConversation"
                    @click="clearConversation"
                  >
                    {{
                      isClearingConversation
                        ? clearingConversationLabel
                        : confirmClearConversationLabel
                    }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
        <Transition name="contact-delete-modal">
          <div
            v-if="showBlockConversationModal"
            class="contact-delete-modal-backdrop"
            tabindex="-1"
            @click.self="closeBlockConversationModal"
            @keydown.esc.stop.prevent="closeBlockConversationModal"
          >
            <div
              class="contact-delete-modal contact-delete-modal--block"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="contact-block-modal-title"
              aria-describedby="contact-block-modal-description"
              @click.stop
            >
              <div class="contact-delete-modal__content">
                <h2
                  id="contact-block-modal-title"
                  class="contact-delete-modal__title"
                >
                  {{ blockConversationTitle }}
                </h2>
                <p
                  id="contact-block-modal-description"
                  class="contact-delete-modal__description"
                >
                  {{ blockConversationDescription }}
                </p>
                <div class="contact-delete-modal__actions">
                  <button
                    type="button"
                    class="contact-delete-modal__cancel"
                    :disabled="isUpdatingConversationBlock"
                    @click="closeBlockConversationModal"
                  >
                    {{ cancelDeleteConversationLabel }}
                  </button>
                  <button
                    type="button"
                    class="contact-delete-modal__confirm"
                    :disabled="isUpdatingConversationBlock"
                    @click="toggleMuteConversation"
                  >
                    {{
                      isUpdatingConversationBlock
                        ? updatingBlockConversationLabel
                        : confirmBlockConversationLabel
                    }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
        <Transition name="contact-delete-modal">
          <div
            v-if="showLeaveGroupModal"
            class="contact-delete-modal-backdrop"
            tabindex="-1"
            @click.self="closeLeaveGroupModal"
            @keydown.esc.stop.prevent="closeLeaveGroupModal"
          >
            <div
              class="contact-delete-modal contact-delete-modal--leave"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="contact-leave-group-modal-title"
              aria-describedby="contact-leave-group-modal-description"
            >
              <div class="contact-delete-modal__content">
                <h2
                  id="contact-leave-group-modal-title"
                  class="contact-delete-modal__title"
                >
                  {{ leaveGroupTitle }}
                </h2>
                <p
                  id="contact-leave-group-modal-description"
                  class="contact-delete-modal__description"
                >
                  {{ leaveGroupDescription }}
                </p>
                <div class="contact-delete-modal__actions">
                  <button
                    type="button"
                    class="contact-delete-modal__cancel"
                    :disabled="isLeavingWhatsappGroup"
                    @click="closeLeaveGroupModal"
                  >
                    {{ cancelDeleteConversationLabel }}
                  </button>
                  <button
                    type="button"
                    class="contact-delete-modal__confirm"
                    :disabled="isLeavingWhatsappGroup"
                    @click="leaveWhatsappGroup"
                  >
                    {{
                      isLeavingWhatsappGroup
                        ? leavingGroupLabel
                        : confirmLeaveGroupLabel
                    }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
        <Transition name="contact-delete-modal">
          <div
            v-if="showDeleteConversationModal"
            class="contact-delete-modal-backdrop"
            tabindex="-1"
            @click.self="closeDeleteConversationModal"
            @keydown.esc.stop.prevent="closeDeleteConversationModal"
          >
            <div
              class="contact-delete-modal contact-delete-modal--delete"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="contact-delete-modal-title"
              aria-describedby="contact-delete-modal-description"
            >
              <div class="contact-delete-modal__content">
                <h2
                  id="contact-delete-modal-title"
                  class="contact-delete-modal__title"
                >
                  {{ deleteConversationTitle }}
                </h2>
                <p
                  id="contact-delete-modal-description"
                  class="contact-delete-modal__description"
                >
                  {{ deleteConversationDescription }}
                </p>
                <div class="contact-delete-modal__actions">
                  <button
                    type="button"
                    class="contact-delete-modal__cancel"
                    :disabled="isDeletingConversation"
                    @click="closeDeleteConversationModal"
                  >
                    {{ cancelDeleteConversationLabel }}
                  </button>
                  <button
                    type="button"
                    class="contact-delete-modal__confirm"
                    :disabled="isDeletingConversation"
                    @click="deleteConversation"
                  >
                    {{
                      isDeletingConversation
                        ? deletingConversationLabel
                        : confirmDeleteConversationLabel
                    }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </Teleport>
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* Aceleres: o painel entra deslizando pela direita (ver ConversationSidebar)
   e, ao chegar, o conteúdo cai de cima para baixo em cascata — mesmo gesto do
   WhatsApp. Um único keyframe atende o painel inteiro; o escalonamento sai do
   laço abaixo, sem repetir regra. */
@keyframes contact-details-slide-down {
  from {
    opacity: 0;
    transform: translateY(-12px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Aceleres 2026-08-19: `backwards` no lugar de `both`.
   Com `both`, o estado final do keyframe fica RETIDO no elemento para sempre —
   e o estado final tem `transform: translateY(0)`, que nao e `none`. Um
   transform, mesmo neutro, cria um contexto de empilhamento: cada sanfona
   passou a ser um mundo proprio, e o `z-index: 9999` do popover de Prioridade
   (MultiselectDropdown) deixou de valer fora da sanfona onde ele vive. As
   sanfonas SEGUINTES — "Etiquetas da conversa", "Campanhas", "Macros" —
   passavam a ser desenhadas por cima do popover aberto, atravessando-o.
   Medido: `both` deixa `matrix(1, 0, 0, 1, 0, 0)`; `backwards` deixa `none`.
   `backwards` mantem o que interessa (o estado inicial durante o
   `animation-delay`, para nao piscar na cascata) e nao retem nada no fim. */
.contact-details-panel__header,
.contact-details-panel__body > *:not(.list-group),
.contact-details-panel__accordion-list > * {
  animation: contact-details-slide-down 260ms ease-out backwards;
}

.contact-details-panel__header {
  animation-delay: 0ms;
}

/* Cabeçalho → blocos do corpo → cada sanfona, 40ms entre eles. */
@for $i from 1 through 4 {
  .contact-details-panel__body > *:not(.list-group):nth-child(#{$i}) {
    animation-delay: #{$i * 40}ms;
  }
}

@for $i from 1 through 10 {
  .contact-details-panel__accordion-list > *:nth-child(#{$i}) {
    animation-delay: #{120 + $i * 40}ms;
  }
}

@media (prefers-reduced-motion: reduce) {
  .contact-details-panel__header,
  .contact-details-panel__body > *:not(.list-group),
  .contact-details-panel__accordion-list > * {
    animation: none;
  }
}

.contact-details-panel__header {
  position: sticky;
  top: 0;
  z-index: 20;
}

.contact-details-panel__header h2 {
  font-size: 1.25rem;
}

.contact-details-panel__header-button {
  width: 48px;
  min-width: 48px;
  height: 48px;
  min-height: 48px;
  border-radius: 9999px;
  color: rgb(var(--slate-12));
  transition:
    background 160ms ease,
    color 160ms ease;
}

.contact-details-panel__header-button:hover,
.contact-details-panel__header-button:focus-visible {
  border-radius: 9999px;
  background: rgba(194, 189, 184, 0.18);
  color: rgb(var(--slate-12));
  outline: none;
}

.contact-details-panel__header-button :deep([class*='i-']) {
  width: 24px;
  height: 24px;
  font-size: 24px;
}

.contact-details-panel__body {
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.42) transparent;
}

.contact-details-panel__body::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}

.contact-details-panel__body::-webkit-scrollbar-track {
  background: transparent;
}

.contact-details-panel__body::-webkit-scrollbar-thumb {
  background-color: rgba(148, 163, 184, 0.42);
  border-radius: 999px;
}

:deep(.contact-details-panel__accordion) {
  width: 100%;
}

:deep(.contact-details-panel__accordion > button) {
  min-height: 62px;
  height: 62px;
  border: 0;
  background: transparent;
  outline: 0;
  cursor: pointer;
}

:deep(.contact-details-panel__accordion > button:hover),
:deep(.contact-details-panel__accordion > button:focus-visible) {
  background: rgba(194, 189, 184, 0.12);
}

:deep(.contact-details-panel__accordion > button > div:first-child) {
  align-items: center;
  gap: 14px;
}

:deep(.contact-details-panel__accordion > button .inline-block) {
  display: none;
}

.contact-details-panel__accordion-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  color: rgba(0, 0, 0, 0.6);
  font-size: 24px;
}

:deep(.contact-details-panel__accordion > button h5) {
  display: inline-flex;
  align-items: center;
  color: rgb(var(--slate-12));
  font-size: 16px;
  font-weight: 500;
}

:deep(.contact-details-panel__accordion > button > div:last-child > div) {
  display: none;
}

:deep(.contact-details-panel__accordion > div) {
  border: 0;
  background: transparent;
  outline: 0;
}

.contact-details-panel__footer-actions {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 0 8px;
  padding: 12px 10px 28px;
  border-top: 0;
}

.contact-details-panel__footer-action {
  display: flex;
  width: 100%;
  min-height: 62px;
  height: 62px;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #202124;
  cursor: pointer;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.25;
  text-align: left;
  transition: background 160ms ease;
}

.contact-details-panel__footer-action:hover,
.contact-details-panel__footer-action:focus-visible {
  background: rgba(217, 45, 32, 0.08);
  outline: none;
}

.contact-details-panel__footer-action--danger {
  color: #d92d20;
}

.contact-details-panel__footer-action--hidden {
  display: none;
}

.contact-details-panel__group-created {
  margin: 4px 0 0;
  margin-left: 0;
  padding: 8px 16px 0 0;
  color: #7a7f86;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.4;
  white-space: nowrap;
}

.contact-details-panel__group-created-value {
  color: inherit;
  font-weight: 400;
}

.contact-details-panel__footer-action-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  color: currentColor;
  font-size: 24px;
}

.contact-delete-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2147483648;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.55);
}

.contact-delete-modal {
  width: min(30rem, calc(100vw - 2rem));
  max-height: calc(100vh - 2rem);
  overflow: auto;
  border-radius: 2rem;
  background: #fff;
  padding: 2rem 2rem 1.5rem;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.24);
}

.contact-delete-modal--block {
  width: min(32rem, calc(100vw - 2rem));
  border-radius: 1.5rem;
  padding: 1.5rem;

  .contact-delete-modal__title {
    font-size: 1.125rem;
  }

  .contact-delete-modal__description {
    margin-top: 1rem;
    font-size: 0.875rem;
  }

  .contact-delete-modal__actions {
    gap: 0.75rem;
    padding-top: 2rem;
  }

  .contact-delete-modal__cancel,
  .contact-delete-modal__confirm {
    min-width: 7rem;
    min-height: 2.5rem;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
  }
}

.contact-delete-modal--clear,
.contact-delete-modal--leave,
.contact-delete-modal--delete {
  width: min(30rem, calc(100vw - 2rem));
}

.contact-delete-modal__content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.contact-delete-modal__title {
  margin: 0;
  color: #111827;
  font-size: 1.5rem;
  font-weight: 500;
  line-height: 1.25;
}

.contact-delete-modal__description {
  max-width: 30rem;
  margin: 1.5rem 0 0;
  color: #6b7280;
  font-size: 1.125rem;
  line-height: 1.5;
}

.contact-delete-modal__actions {
  display: flex;
  width: 100%;
  justify-content: flex-end;
  gap: 1.5rem;
  padding-top: 3rem;
}

.contact-delete-modal__cancel,
.contact-delete-modal__confirm {
  display: inline-flex;
  min-width: 9.5rem;
  min-height: 3.5rem;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  padding: 0.75rem 1.75rem;
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.25;
}

.contact-delete-modal__cancel {
  border: 0;
  background: transparent;
  color: #278c68;
  border-radius: 50px;
}

.contact-delete-modal__cancel:hover:not(:disabled) {
  background: #e7f5ed;
  color: #1f7053;
}

.contact-delete-modal__confirm {
  border: 0;
  background: #f0003c;
  color: #fff;
}

.contact-delete-modal__cancel:disabled,
.contact-delete-modal__confirm:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.contact-delete-modal-enter-active,
.contact-delete-modal-leave-active {
  transition: opacity 0.2s ease;
}

.contact-delete-modal-enter-from,
.contact-delete-modal-leave-to {
  opacity: 0;
}

@media (max-width: 767px) {
  .contact-details-panel {
    min-height: 0;
    max-width: 100%;
  }

  .contact-details-panel__body {
    min-width: 0;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }

  .contact-details-panel__group-created {
    white-space: normal;
    overflow-wrap: anywhere;
  }
}

@media (max-width: 640px) {
  .contact-delete-modal {
    width: min(30rem, calc(100vw - 1rem));
    padding: 2rem 1.25rem 1.5rem;
  }

  .contact-delete-modal--block {
    padding: 1.5rem 1.25rem 1.25rem;
  }

  .contact-delete-modal__actions {
    flex-direction: column-reverse;
  }

  .contact-delete-modal__cancel,
  .contact-delete-modal__confirm {
    width: 100%;
  }
}

:deep(.contact--profile) {
  @apply pb-3 border-b border-solid border-n-weak;
}

/* Aceleres 2026-08-19 -- o cabecalho do telemovel.
   No computador nada disto existe: a seta, o par foto+nome e o menu de tres
   pontos ficam escondidos e o titulo continua onde estava. */
.contact-details-panel__header-button--back,
.contact-details-panel__mini,
.contact-details-panel__header-menu {
  display: none;
}

.contact-details-panel__header-menu {
  position: relative;
}

.contact-details-panel__header-menu-list {
  position: absolute;
  top: calc(100% + 6px);
  inset-inline-end: 0;
  z-index: 30;
  min-width: 200px;
}

/*
 * Este bloco contém os accordions legados da antiga aba "Detalhes": Notas do
 * contato, atributos, informações da conversa, ações, campanhas e Macros.
 *
 * Ele permanece montado de propósito, para não perder estado, listeners ou
 * funcionalidades. A classe usa `!important` porque o container também recebe
 * classes utilitárias de layout que podem declarar `display` com prioridade
 * maior que o `display: none` inline produzido por `v-show`.
 */
.contact-details-panel__actions-hidden {
  display: none !important;
}

@media (max-width: 767px) {
  /* Sem titulo, sem X: seta a esquerda, tres pontos a direita. O lapis de
     editar passa para dentro do menu — e a mesma accao, num sitio so. */
  .contact-details-panel__title,
  .contact-details-panel__header-button--close,
  .contact-details-panel__header-button--edit {
    display: none;
  }

  .contact-details-panel__header-button--back,
  .contact-details-panel__header-menu {
    display: inline-flex;
  }

  /* A foto pequena e o nome entram acompanhando a rolagem: a progressao chega
     por `--acl-hero-progresso`, publicada na raiz do painel. Nao ha `transition`
     nenhuma aqui de proposito — o valor ja muda a cada evento de scroll, e uma
     transicao por cima faria o par correr atras do dedo. O caminho de volta e
     o mesmo, ao contrario. */
  .contact-details-panel__mini {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 8px;
    opacity: var(--acl-hero-progresso, 0);
    transform: translate3d(
      calc((1 - var(--acl-hero-progresso, 0)) * -10px),
      0,
      0
    );
    pointer-events: none;
  }

  .contact-details-panel__mini-name {
    overflow: hidden;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.2;
    color: rgb(var(--slate-12));
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* O nome so ocupa lugar quando ja esta a aparecer; assim, no topo da ficha, a
     seta fica sozinha como num cabecalho vazio. */
  .contact-details-panel:not(.contact-details-panel--hero-colapsado)
    .contact-details-panel__mini {
    max-width: 0;
  }
}
</style>
